/**
 * Permsissions collection
 * This collection contains all permissions for a given resource [Asset, Attribute, Attribute Set, Category, Cart, Module, Search]
 * @class
 */
import Base from '@netx/core/lib/netx-collection';
import Model from '../models/permission';

import PagerModel from '@netx/core-app/models/pager';
import PermissionsParticipants from './permissionsParticipants';

export default class Permissions extends Base {
	/**
	 * @type {Netx.Rpc}
	 * @private
	 */
	get rpc() {
		return true;
	}

	/** @type {boolean} */
	get xhrTrackProgress() {
		return true;
	}

	/**
	 * @property {object} methods       - Collection RPC methods
	 * @property {array}  methods.read  - Get all permissions for resource
	 */
	get methods() {
		return {
			read(opts) {
				return [
					'getPermissionsByResource',
					this.resourceName,
					this.resourceId,
					this.pager.getPageDTO(),
					this.modeFilters,
				];
			},
		};
	}

	/**
	 * @property {object}   parsers       - Collection parsers
	 * @property {function} parsers.read  - Parse permissions
	 */
	get parsers() {
		return {
			/**
			 * Make sure we do not have duplicate modes
			 * @function
			 * @param {array} permissions Permissions
			 * @param {object} opts Options
			 */
			read(permissions, opts) {
				const uniquePerms = [];
				let groupIds = [];
				let userIds = [];

				_.each(permissions, (permission) => {
					// No deny permissions
					if (permission.type === 'deny') {
						this.toRemove.push(permission.permissionId);
						return;
					}

					let selectors = permission.userSelector;

					const existing = _.findWhere(uniquePerms, {mode: permission.mode});
					if (!existing) {
						userIds = userIds.concat(selectors.userIds);
						groupIds = groupIds.concat(selectors.groupIds);
						uniquePerms.push(permission);
						return;
					}

					// Remove the duplicate
					this.toRemove.push(permission.permissionId);
					// Update the existing
					this.toSave.push(existing.permissionId);

					const existingSelectors = existing.userSelector;

					// Do not let a user be in multiple permission modes
					const newGroupIds = _.without(
						selectors.groupIds,
						_.intersection(userIds, selectors.groupIds),
					);
					const newUserIds = _.without(
						selectors.userIds,
						_.intersection(userIds, selectors.userIds),
					);

					// Merge user selectors
					existing.userSelector = {
						all: existingSelectors.all || selectors.all,
						groupIds: existingSelectors.groupIds.concat(newGroupIds),
						userIds: existingSelectors.userIds.concat(newUserIds),
					};
				});

				return uniquePerms;
			},
		};
	}

	/** @override */
	constructor(models, opts = {}) {
		opts.model = opts.model || Model;

		super(models, opts);

		/**
		 * Paging model
		 * @memberOf Netx.Collections.Users
		 * @alias pager
		 * @type {Netx.Models.Pager}
		 * @todo We need to trigger unsaved changes warning for when paging permissions
		 */
		this.pager = new PagerModel({pageSize: 0x7fffffff}, {collection: this});

		/**
		 * Mode filters (read/write/delete/favorite)
		 * @memberOf Netx.Collections.Permissions
		 * @alias modeFilters
		 * @type {object}
		 */
		this.modeFilters = opts.modeFilters || null;

		/**
		 * Resource name
		 * @memberOf Netx.Collections.Permissions
		 * @alias resourceName
		 * @type {string}
		 */
		this.resourceName = opts.resourceName || '';

		/**
		 * Resource id
		 * @memberOf Netx.Collections.Permissions
		 * @alias resourceId
		 * @type {number}
		 */
		this.resourceId = opts.resourceId || null;

		/**
		 * A layer for making permissions play like participants in 8.12
		 * @memberof module:permissions/collections/permissions
		 * @alias participants
		 * @type {module:permissions/collections/permissionsParticipants}
		 */
		this.participants = new PermissionsParticipants([], {
			collection: this,
			resourceId: this.resourceId,
			resourceName: this.resourceName,
		});

		/**
		 * Store models that need to be created on the server
		 * @memberof module:permissions/collections/permissions
		 * @alias toAdd
		 * @type {array}
		 */
		this.toAdd = [];
		/**
		 * Store model ids that need to be updated on the server
		 * @memberof module:permissions/collections/permissions
		 * @alias toSave
		 * @type {array}
		 */
		this.toSave = [];
		/**
		 * Store model ids that need to be destroyed on the server
		 * @memberof module:permissions/collections/permissions
		 * @alias toRemove
		 * @type {array}
		 */
		this.toRemove = [];

		// Hey, listen!
		// For making permissions play like participants in 8.12
		this.listenTo(this, 'request:read', (collection, xhr, opts) => {
			// Reset
			this.stopListening(this, 'add remove change sync');
			this.participants.reset([]);

			this.listenTo(this, 'sync', (collection) => {
				// Could be an underlying model
				if (collection !== this) return;

				// Add existing records
				this.each(this.participants.addByPermission, this.participants);

				// Keep track additions, removals and changes
				this.listenTo(this, 'add', (model, collection) => {
					// If we do not already have and id, we need to be created on the server
					!model.id && this.toAdd.push(model);
				})
					.listenTo(this, 'remove', (model, collection) => {
						let idx;
						// If we were new and removed before ever being saved,
						// we no longer need to be created on the server
						if (!model.id) {
							idx = this.toAdd.indexOf(model);
							return idx > -1 && this.toAdd.splice(idx, 1);
						}

						idx = this.toSave.indexOf(model.id);
						idx > -1 && this.toSave.splice(idx, 1);
						this.toRemove.push(model.id);
					})
					.listenTo(this, 'change', (model) => {
						// If the model does not have an id then we have not been created
						// on the server yet and do not need saving
						if (!model.id) return;
						this.toSave.indexOf(model.id) < 0 && this.toSave.push(model.id);
					});
			});
		});
	}

	/**
	 * Add a new default permission for this resource
	 * The permission is not yet saved - just added to the collection
	 * @method
	 * @param {object} attrs Attributes
	 * @param {object} opts Options
	 */
	addPermission(attrs = {}, opts = {}) {
		return this.add(
			_.extend(attrs, {
				resource: {
					id: this.resourceId,
					type: this.resourceName,
				},
			}),
			opts,
		);
	}

	/**
	 * @method
	 * @param {object} opts Options
	 */
	save(opts = {}) {
		let toRemove = this.toRemove;
		let toAdd = this.toAdd;
		let toSave = this.toSave;

		// Part of the particpants hack for this collection
		// We will trigger a sync when done
		this.stopListening(this, 'add remove change sync');

		// All permissions with empty userSelectors will be set for removal
		this.forEach((perm) => {
			if (perm.hasSelectors()) return;
			!_.contains(toRemove, perm.id) && toRemove.push(perm.id);
		});

		// Remove
		const removePromises = _.compact(
			_.map(toRemove, (id) => {
				const model = this.get(id) || new this.model({permissionId: id});
				return model.destroy(_.extend({wait: true}, opts));
			}),
		);

		// Create
		const addPromises = _.compact(
			_.map(toAdd, (model) => {
				// We will not be adding it if there are no selectors
				if (!model.hasSelectors() || model.id) return;
				return model.save(opts);
			}),
		);

		// Update
		const savePromises = _.compact(
			_.map(toSave, (id) => {
				// We will not be updating it if it was removed
				if (_.contains(toRemove, id)) return;
				const model = this.get(id);
				return model && model.save(opts);
			}),
		);

		this.toRemove = [];
		this.toAdd = [];
		this.toSave = [];

		const allPromises = removePromises.concat(addPromises, savePromises);

		const p = this.when.apply(this, allPromises);
		p.then(() => {
			!opts.silent && allPromises.length && this.trigger('sync', this, opts);
		});

		return p;
	}

	/** @type {string} */
	static get __name__() {
		return 'Permissions';
	}
}
